
    import Vue from "vue";
    import Component from "vue-class-component";
    import { required, email } from "vuelidate/lib/validators";
    import { Validations } from "vuelidate-property-decorators";
    import SlideUpDown from "vue-slide-up-down";
    import ApiButton from "@/components/ApiButton.vue";

    import apiClient from "@/stuff/ApiClient";
    import { User, IUser } from "@/model/User";
    import { ISendPasswordResetRequest } from "@/model/ISendPasswordResetRequest";
    import { ISendPasswordResetResponse } from "@/model/ISendPasswordResetResponse";
    import * as Toastr from "toastr";
    import { Authentication } from "@/stuff/Authentication";
    import { AccountInfo, EndSessionRequest, EventType, InteractionRequiredAuthError, PublicClientApplication, RedirectRequest } from "@azure/msal-browser"
import store from "@/store/store";

    // We could put these interfaces (or classes) in their own files - but they're small 
    // and so far they're only used here.

    interface ISignInRequest {
        email: string;
        password: string;
    }

    export interface ISignInResponse {
        isSuccess: boolean;
        message: string;
        user: IUser;
    }

    // https://vuejs.org/v2/guide/typescript.html

    @Component({
        components: { SlideUpDown, ApiButton }
    })     
    export default class SignInForm extends Vue {

        msal = Vue.prototype.$msal as PublicClientApplication;

        // Lifecycle hook
        mounted() {
            this.msal.handleRedirectPromise();
            this.loadFromStorage()
        }

        signInRequest: ISignInRequest = { email: "", password: "" };

        sendPasswordResetRequest: ISendPasswordResetRequest = {
            email: "",
            isSelfInitiated: true
        };

        isSendPasswordResetMode = false;

        messageText = "";

        get isMessage(): boolean {
            return !!this.messageText;
        }

        // get properties are 'computeds'
        get isAuthenticated(): boolean {
            return this.$store.state.signedInUser !== null;
        }

        get userName(): string {
            const user = this.$store.state.signedInUser as User;
            if (user === null) return "not logged in";
            return `${user.forename} ${user.surname}`;
        }

        toggleMode() {
            this.$v.$reset(); // reset validation
            this.messageText = "";
            if (!this.isSendPasswordResetMode && this.signInRequest.email) {
                // copy the email over to save user typing it out again
                this.sendPasswordResetRequest.email = this.signInRequest.email;
            }
            this.isSendPasswordResetMode = !this.isSendPasswordResetMode;
        }

        async signIn(event: Event) {
            // TODO - if this triggered from enter key, we could do with finding anad passing the button...
            
            // 'touch' all the fields to activate the validation messages
            this.$v.$touch();

            if (this.$v.signInRequest.$invalid) {
                Toastr.error("Please fix the highlighted errors", "Can't sign-in");
                return;
            }

            const response: ISignInResponse = await apiClient.post("/Api/Authentication/signin", this.signInRequest, event);

            await Authentication.signIn(new User(response.user));

            if (this.isAuthenticated) {
                this.saveToStorage(response.user);
                console.log("...SignInForm component - emitting is-authenticated");
                this.$emit("is-authenticated");
            }
            else {
                this.messageText = response.message;
            }
        }

        async vistrySignIn(): Promise<void> {
            Vue.prototype.$msal.loginPopup({ scopes: [ store.state.vistrySsoScope ] })
                .then(this.postSignInLogic);

        }

        private async postSignInLogic()  {
            const msal = Vue.prototype.$msal as PublicClientApplication;

            // check we've got a authorised account;
            if (msal.getAllAccounts().length == 0) Authentication.signOut();

            // set active account
            const account = msal.getAllAccounts()[0];            
            msal.setActiveAccount(account);

            // fetch account jwt
            let token = "";
            const tokenRequest = { scopes: [ store.state.vistrySsoScope ], account: account }
            try {
                const tokenResponse = await msal.acquireTokenSilent(tokenRequest);
                token = tokenResponse.accessToken;
            }
            catch(err) {
                if (err instanceof InteractionRequiredAuthError) {
                    const tokenResponse = await msal.acquireTokenPopup(tokenRequest);
                    token = tokenResponse.accessToken;
                }
                else {
                    throw err
                }
            }

            // clear user if no token found
            if (!token) {
                Authentication.signOut();
                return;
            }

            // log user in 
            const response: ISignInResponse = await apiClient.get("api/vistry/signin", undefined, token);
           
            if (response.isSuccess) {
                await Authentication.signIn(new User(response.user));
                this.saveToStorage(response.user);
                console.log("...SignInForm component - emitting is-authenticated");
                this.$emit("is-authenticated");
            }
            else {
                this.messageText = response.message;
            }
        }

        async submitSendPasswordReset() {
            this.$v.$touch();
            if (this.$v.sendPasswordResetRequest.$invalid) {
                Toastr.error("Please fix the highlighted error");
                return;
            }
            const response: ISendPasswordResetResponse = await apiClient.post("/Api/Authentication/sendPasswordReset", this.sendPasswordResetRequest);
            this.messageText = response.message;
        }

        private loadFromStorage() {
            if (typeof (Storage) === "undefined") return;
            const userJson = localStorage.getItem("user");
            if (!userJson) return;
            try {
                const user = JSON.parse(userJson);
                this.signInRequest.email = (user && user.email) ? user.email : "";
                this.sendPasswordResetRequest.email = this.signInRequest.email;
            } catch (e) {
                this.signInRequest.email = "";
            }
        }

        private saveToStorage(user: IUser) {
            if (typeof (Storage) === "undefined") return;
            localStorage.setItem("user", JSON.stringify(user));
        }

        @Validations()
        validations() {
            return {
                signInRequest: {
                    email: { required, email },
                    password: { required }
                },
                sendPasswordResetRequest: {
                    email: { required, email }
                }
            };
        }
    }

